import { forwardRef, HTMLAttributes, ReactNode } from "react";

export type Props = HTMLAttributes<HTMLDivElement> & {
  /** Whether the check circle is in disabled/inactive state */
  isDisabled?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the icon */
  iconClassName?: string;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Render prop for the check icon */
  renderIcon?: (props: { isDisabled: boolean; className?: string }) => ReactNode;
};

/**
 * Base check circle component with no styling.
 * Displays a circular check indicator, typically used for success states.
 * Uses render props for the icon to allow customization.
 */
export const CheckCircleBase = forwardRef<HTMLDivElement, Props>(
  (
    {
      isDisabled = false,
      className,
      iconClassName,
      "data-testid": testId = "check-circle-base",
      renderIcon,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={className}
        data-testid={testId}
        data-disabled={isDisabled}
        {...props}
      >
        {renderIcon ? (
          renderIcon({ isDisabled, className: iconClassName })
        ) : (
          <span data-testid={`${testId}-icon`}>✓</span>
        )}
      </div>
    );
  }
);

CheckCircleBase.displayName = "CheckCircleBase";

